import { computed, Ref } from 'vue';
import {
  CabinTypeLabel,
  RoofTypeLabel,
  DrivetrainLabel,
  EngineTypeLabel,
  CabinVariations,
  RoofVariations,
  SizeEngineVariations,
  SizeVariations,
  WeightMax,
  DrivetrainTypesLabel,
  EngineTypesLabel,
} from '@/types';

export function useAvailableOptions(
  vehicleType: string,
  variation: Ref<
    CabinVariations | RoofVariations | SizeEngineVariations | SizeVariations
  >,
  selectedRoof: Ref<RoofTypeLabel | null>,
  selectedCabin: Ref<CabinTypeLabel | null>,
  selectedWeight: Ref<number | null>,
  selectedEngine: Ref<EngineTypeLabel | null>,
) {
  const available_weights = computed(() => {
    // Get available weights based on current selections
    const filterWeightsByEngine = (weightsMax: WeightMax[] = []) => {
      return weightsMax
        .filter((weightObj: WeightMax) =>
          weightObj.drivetrainTypes.some((drivetrain: DrivetrainTypesLabel) =>
            drivetrain.engineTypes.some(
              (engine: EngineTypesLabel) =>
                engine.engineType === selectedEngine.value,
            ),
          ),
        )
        .map((weightObj: WeightMax) => weightObj.weightMax);
    };

    switch (vehicleType) {
      case 'Sprinter_Cabin': {
        const cabinVariation = variation.value as CabinVariations;
        const selectedCabinTypes = cabinVariation.cabinTypes.filter(
          (cabin) => cabin.cabinType === selectedCabin.value,
        );

        return selectedCabinTypes.flatMap((cabin) =>
          filterWeightsByEngine(cabin.weightsMax),
        );
      }

      case 'Sprinter_Roof': {
        const roofVariation = variation.value as RoofVariations;
        const selectedRoofTypes = roofVariation.roofHeightTypes.filter(
          (roof) => roof.roofHeightType === selectedRoof.value,
        );

        return selectedRoofTypes.flatMap((roof) =>
          filterWeightsByEngine(roof.weightsMax),
        );
      }

      case 'Vito':
      case 'Citan': {
        const sizeVariation = variation.value as SizeVariations;
        if (!sizeVariation.weightsMax) return [];

        return filterWeightsByEngine(sizeVariation.weightsMax);
      }

      default:
        return [];
    }
  });

  const available_cabins = computed(() => {
    if (vehicleType === 'Sprinter_Cabin') {
      if (selectedWeight.value === null) return [];
      return (
        (variation.value as CabinVariations).cabinTypes
          .filter((e) =>
            e.weightsMax
              .map((e) => e.weightMax)
              .includes(selectedWeight.value as number),
          )
          .map((e) => e.cabinType) || []
      );
    } else {
      return [];
    }
  });

  const available_roofs = computed(() => {
    if (vehicleType === 'Sprinter_Roof') {
      if (selectedWeight.value === null) return [];
      return (
        (variation.value as RoofVariations).roofHeightTypes
          .filter((e) =>
            e.weightsMax
              .map((e) => e.weightMax)
              .includes(selectedWeight.value as number),
          )
          .map((e) => e.roofHeightType) || []
      );
    } else {
      return [];
    }
  });

  const available_engines = computed(() => {
    switch (vehicleType) {
      case 'Sprinter_Cabin': {
        if (selectedWeight.value === null) return [];
        const cabinVariation = variation.value as CabinVariations;
        const weightObj = cabinVariation.cabinTypes
          .filter((e) => e.cabinType === selectedCabin.value)
          .flatMap((e) => e.weightsMax)
          .find((weightObj) => weightObj.weightMax === selectedWeight.value);

        return weightObj
          ? weightObj.drivetrainTypes
              .flatMap((e) => e.engineTypes)
              .map((e) => e.engineType)
          : [];
      }
      case 'Sprinter_Roof': {
        if (selectedWeight.value === null) return [];
        const roofVariation = variation.value as RoofVariations;
        const weightObj = roofVariation.roofHeightTypes
          .filter((e) => e.roofHeightType === selectedRoof.value)
          .flatMap((e) => e.weightsMax)
          .find((weightObj) => weightObj.weightMax === selectedWeight.value);

        return weightObj
          ? weightObj.drivetrainTypes
              .flatMap((e) => e.engineTypes)
              .map((e) => e.engineType)
          : [];
      }
      case 'Vito': {
        if (selectedWeight.value === null) return [];
        const sizeVariation = variation.value as SizeVariations;
        const weightObj = sizeVariation.weightsMax?.find(
          (weightObj) => weightObj.weightMax === selectedWeight.value,
        );
        return weightObj
          ? weightObj.drivetrainTypes.flatMap((e) =>
              e.engineTypes.map((e) => e.engineType),
            )
          : [];
      }
      case 'Citan': {
        const sizeEngineVariation = variation.value as SizeEngineVariations;
        return sizeEngineVariation.engineTypes.map((e) => e.engineType);
      }
      default:
        return [];
    }
  });

  const available_batteryCapacities = computed(() => {
    switch (vehicleType) {
      case 'Sprinter_Cabin': {
        if (selectedWeight.value === null) return [];
        const selectedCabinType = (
          variation.value as CabinVariations
        ).cabinTypes.find((e) => e.cabinType === selectedCabin.value);

        if (!selectedCabinType) return [];

        const selectedCabinWeightObj = selectedCabinType.weightsMax.find(
          (weightObj) => weightObj.weightMax === selectedWeight.value,
        );

        if (!selectedCabinWeightObj) return [];

        return selectedCabinWeightObj.drivetrainTypes
          .flatMap((e) => e.engineTypes)
          .flatMap((e) => e.batteryCapacities || []);
      }
      case 'Sprinter_Roof': {
        if (selectedWeight.value === null) return [];
        const selectedRoofType = (
          variation.value as RoofVariations
        ).roofHeightTypes.find((e) => e.roofHeightType === selectedRoof.value);

        if (!selectedRoofType) return [];

        const selectedRoofWeightObj = selectedRoofType.weightsMax.find(
          (weightObj) => weightObj.weightMax === selectedWeight.value,
        );

        if (!selectedRoofWeightObj) return [];

        return selectedRoofWeightObj.drivetrainTypes
          .flatMap((e) => e.engineTypes)
          .flatMap((e) => e.batteryCapacities || []);
      }
      case 'Vito': {
        if (selectedWeight.value === null) return [];
        const sizeVariation = variation.value as SizeVariations;
        return (
          sizeVariation.weightsMax?.flatMap((e) =>
            e.drivetrainTypes
              .flatMap((e) => e.engineTypes)
              .flatMap((e) => e.batteryCapacities || []),
          ) || []
        );
      }
      case 'Citan': {
        const sizeEngineVariation = variation.value as SizeEngineVariations;
        return sizeEngineVariation.engineTypes.flatMap(
          (e) => e.batteryCapacities || [],
        );
      }
      default:
        return [];
    }
  });

  const available_drivetrains = computed(() => {
    switch (vehicleType) {
      case 'Sprinter_Cabin': {
        if (selectedWeight.value === null) return [];
        const cabinTypes = (
          variation.value as CabinVariations
        ).cabinTypes.filter((e) => e.cabinType === selectedCabin.value);

        if (!cabinTypes.length) return [];

        const weightObjs = cabinTypes
          .flatMap((e) => e.weightsMax)
          .filter((weightObj) => weightObj.weightMax === selectedWeight.value);

        if (!weightObjs.length) return [];

        return weightObjs[0].drivetrainTypes.map((e) => e.drivetrainType);
      }
      case 'Sprinter_Roof': {
        if (selectedWeight.value === null) return [];
        const roofTypes = (
          variation.value as RoofVariations
        ).roofHeightTypes.filter(
          (e) => e.roofHeightType === selectedRoof.value,
        );

        if (!roofTypes.length) return [];

        const weightObjs = roofTypes
          .flatMap((e) => e.weightsMax)
          .filter((weightObj) => weightObj.weightMax === selectedWeight.value);

        if (!weightObjs.length) return [];

        return weightObjs[0].drivetrainTypes.map((e) => e.drivetrainType);
      }
      case 'Vito':
      case 'Citan': {
        const sizeVariation = variation.value as SizeVariations;
        if (!sizeVariation.weightsMax) return [];
        return sizeVariation.weightsMax.flatMap((e) =>
          e.drivetrainTypes.map((e) => e.drivetrainType),
        );
      }
      default:
        return [];
    }
  });

  return {
    available_weights,
    available_cabins,
    available_roofs,
    available_engines,
    available_batteryCapacities,
    available_drivetrains,
  };
}
